import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  IconButton,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axiosInstance from 'utils/axiosUtil';
import { 
  downloadMarketDataPDF,
  previewMarketDataPDF,
  buildReportTitle,
} from 'utils/pdf-export';

const styles = (theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '95vw',
      width: '95vw',
      height: '90vh',
      maxHeight: '90vh',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
  },
  titleText: {
    fontSize: 18,
    fontWeight: 500,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    height: 'calc(100% - 130px)',
    overflow: 'hidden',
  },
  settingsBar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: 'wrap',
  },
  rowCountContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  rowCountInput: {
    width: 80,
  },
  titleFieldContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 300,
  },
  titleField: {
    flex: 1,
    '& .MuiInputBase-input': {
      fontSize: 13,
    },
  },
  columnSelectSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  columnSelectHeader: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  columnSelectTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  columnCheckboxes: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  columnCheckbox: {
    '& .MuiFormControlLabel-label': {
      fontSize: 13,
    },
    '& .MuiCheckbox-root': {
      padding: 4,
    },
  },
  previewPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
    overflow: 'hidden',
  },
  previewHeader: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[200],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  previewTitle: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  previewFrame: {
    flex: 1,
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundColor: '#525659',
  },
  previewLoading: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  previewEmpty: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  actions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  downloadButton: {
    minWidth: 140,
  },
  // Custom pages styles
  customPagesSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  customPagesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  customPagesTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  customPagesList: {
    padding: 0,
    '& .MuiListItem-root': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      borderRadius: 4,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
  customPageItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  customPageType: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[100],
    padding: '2px 6px',
    borderRadius: 4,
  },
  customPagePosition: {
    fontSize: 10,
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  addPageButton: {
    fontSize: 12,
    textTransform: 'none',
  },
  // Page editor dialog
  pageEditorField: {
    marginBottom: theme.spacing(2),
  },
  pageEditorContent: {
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      fontSize: 12,
    },
  },
});

// PDF Type configurations
const PDF_TYPES = {
  printable: {
    label: 'Printable',
    orientation: 'landscape',
    format: 'letter',
    pageSize: 'letter',
  },
  slideshow: {
    label: 'Presentation',
    orientation: 'landscape',
    format: [16, 9],  // 16:9 aspect ratio (will be scaled)
    pageSize: [792, 445.5],  // 11" x 6.1875" (16:9 at 72dpi)
  },
};

// Column configurations by report type
const COLUMN_CONFIGS = {
  projected_patients: {
    providers: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Provider Name', default: true },
      { id: 'credential', label: 'Credential', default: false },
      { id: 'specialty', label: 'Specialty', default: true },
      { id: 'organization', label: 'Organization', default: true },
      { id: 'soc', label: 'Site of Care', default: true },
      { id: 'patients', label: 'Patients', default: true },
      { id: 'marketPct', label: 'Market %', default: true },
      { id: 'chart', label: 'Chart', default: true },
    ],
    organizations: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Organization Name', default: true },
      { id: 'facilityType', label: 'Facility Type', default: true },
      { id: 'soc', label: 'Site of Care', default: true },
      { id: 'patients', label: 'Patients', default: true },
      { id: 'marketPct', label: 'Market %', default: true },
      { id: 'chart', label: 'Chart', default: true },
    ],
  },
  captured_claims: {
    providers: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Provider Name', default: true },
      { id: 'credential', label: 'Credential', default: false },
      { id: 'specialty', label: 'Specialty', default: true },
      { id: 'organization', label: 'Organization', default: true },
      { id: 'patients', label: 'Claims', default: true },
      { id: 'marketPct', label: 'Market %', default: true },
      { id: 'chart', label: 'Chart', default: true },
    ],
    organizations: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Organization Name', default: true },
      { id: 'facilityType', label: 'Facility Type', default: true },
      { id: 'patients', label: 'Claims', default: true },
      { id: 'marketPct', label: 'Market %', default: true },
      { id: 'chart', label: 'Chart', default: true },
    ],
  },
  shared_affiliation: {
    providers: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Provider Name', default: true },
      { id: 'credential', label: 'Credential', default: false },
      { id: 'specialty', label: 'Specialty', default: true },
      { id: 'organization', label: 'Organization', default: true },
      { id: 'affiliatedPct', label: '% Affiliated', default: true },
      { id: 'chart', label: 'Chart', default: true },
    ],
    organizations: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Organization Name', default: true },
      { id: 'facilityType', label: 'Facility Type', default: true },
      { id: 'affiliatedPct', label: '% Affiliated', default: true },
      { id: 'chart', label: 'Chart', default: true },
    ],
    servicelines: [
      { id: 'name', label: 'Service Line', default: true },
      { id: 'patients', label: 'Patients', default: true },
      { id: 'affiliatedPct', label: '% Affiliated', default: true },
      { id: 'chart', label: 'Chart', default: true },
    ],
  },
  shared_patients: {
    providers: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Provider Name', default: true },
      { id: 'specialty', label: 'Specialty', default: true },
      { id: 'organization', label: 'Organization', default: true },
      { id: 'patients', label: 'Shared Patients', default: true },
      { id: 'pct', label: '% Shared', default: true },
      { id: 'chart', label: 'Chart', default: true },
    ],
    servicelines: [
      { id: 'name', label: 'Service Line', default: true },
      { id: 'pct', label: '% Shared', default: true },
      { id: 'patients', label: 'Shared Patients', default: true },
      { id: 'chart', label: 'Chart', default: true },
    ],
  },
  payors: {
    providers: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Provider Name', default: true },
      { id: 'credential', label: 'Credential', default: false },
      { id: 'specialty', label: 'Specialty', default: true },
      { id: 'organization', label: 'Organization', default: true },
      { id: 'chart', label: 'Payor Bars', default: true },
    ],
    organizations: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Organization Name', default: true },
      { id: 'facilityType', label: 'Facility Type', default: true },
      { id: 'chart', label: 'Payor Bars', default: true },
    ],
  },
  payor_mix: {
    providers: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Provider Name', default: true },
      { id: 'credential', label: 'Credential', default: false },
      { id: 'specialty', label: 'Specialty', default: true },
      { id: 'organization', label: 'Organization', default: true },
      { id: 'chart', label: 'Payor Bars', default: true },
    ],
    organizations: [
      { id: 'icon', label: 'Affiliated Icon', default: true },
      { id: 'name', label: 'Organization Name', default: true },
      { id: 'facilityType', label: 'Facility Type', default: true },
      { id: 'chart', label: 'Payor Bars', default: true },
    ],
  },
};

// Helper to get default column selection
const getDefaultColumns = (reportType, claimsType) => {
  const config = COLUMN_CONFIGS[reportType] || COLUMN_CONFIGS.projected_patients;
  let columns;
  if (claimsType === 'servicelines' && config.servicelines) {
    columns = config.servicelines;
  } else if (claimsType === 'organizations') {
    columns = config.organizations;
  } else {
    columns = config.providers;
  }
  const defaults = {};
  columns.forEach(col => {
    defaults[col.id] = col.default;
  });
  return defaults;
};

class PDFExportDialog extends React.Component {
  constructor(props) {
    super(props);
    
    // Calculate initial max rows (can't use class method in constructor)
    let initialCount = props.totalCount;
    if (!initialCount || initialCount < 1) {
      initialCount = (props.chartData && props.chartData.length) || 200;
    }
    // Cap reflects the true ceiling (totalCount/chartData.length, not 200).
    // If `defaultMaxRows` is provided by the caller (e.g. the on-screen row
    // count), use that as the initial slider value; otherwise default to the
    // full cap.
    const initialMaxRows = (props.defaultMaxRows && props.defaultMaxRows > 0)
      ? Math.min(props.defaultMaxRows, initialCount)
      : initialCount;
    
    // Determine claimsType for column config selection
    const claimsType = props.defaultOptions?.claimsType || 'providers';
    const reportType = props.reportType || 'projected_patients';
    
    this.state = {
      pdfType: 'printable',  // Default to printable
      customTitle: '',  // Will be computed when dialog opens
      customSubtitle: props.subtitle || '',  // Editable subtitle
      titleModified: false,  // Track if user has modified title
      selectedColumns: getDefaultColumns(reportType, claimsType),
      options: {
        orientation: 'landscape',
        pageSize: 'letter',  // Printable uses letter
        includeHeader: true,
        includeFooter: true,
        includeChart: true,
        includeTable: false,
        maxRows: initialMaxRows,
        ...props.defaultOptions,
      },
      // Custom pages feature
      customPages: [],  // Array of { id, position, title, subtitle, components }
      editingPage: null,  // Page being edited (null = not editing)
      isGeneratingAI: false,  // AI suggestion loading state
      // Popup dialogs
      columnsDialogOpen: false,
      pagesDialogOpen: false,
      previewUrl: null,
      isGenerating: false,
      error: null,
    };
    
    this.generatePreviewTimer = null;
    this.titleChangeTimer = null;
  }

  /**
   * Compute the title that will be used in the PDF
   * Uses the same logic as the PDF generator
   */
  getComputedTitle = () => {
    const { title, reportType, filters, defaultOptions } = this.props;
    const { pdfType, options } = this.state;
    
    return buildReportTitle({
      title,
      reportType,
      filters,
      options: {
        ...options,
        ...defaultOptions,
        pdfType,
      },
    });
  };

  getValidMaxRows = (totalCount, chartData) => {
    // Use the actual available count (totalCount when provided, otherwise the
    // length of chartData). No artificial 200 ceiling — callers decide what's
    // safe by what they pass in.
    let count = totalCount;
    if (!count || count < 1) {
      count = (chartData && chartData.length) || 200;
    }
    return count;
  };

  getTotalCount = () => {
    const { totalCount, chartData } = this.props;
    if (totalCount && totalCount > 0) {
      return totalCount;
    }
    return (chartData && chartData.length) || 0;
  };

  componentDidUpdate(prevProps, prevState) {
    const { open, chartData, totalCount, defaultOptions, reportType, subtitle } = this.props;
    const { options, pdfType, titleModified, customTitle, customSubtitle, selectedColumns } = this.state;
    
    // Determine claimsType for column config selection
    const claimsType = defaultOptions?.claimsType || 'providers';
    const prevClaimsType = prevProps.defaultOptions?.claimsType || 'providers';
    
    // Clear preview and reset when dialog opens fresh, reportType changes, or claimsType changes
    if ((open && !prevProps.open) || reportType !== prevProps.reportType || claimsType !== prevClaimsType) {
      // Clear old preview
      if (this.state.previewUrl) {
        URL.revokeObjectURL(this.state.previewUrl);
      }
      // Compute the title that will actually appear in the PDF
      const computedTitle = this.getComputedTitle();
      // Reset column selection to defaults for this report type
      this.setState({ 
        previewUrl: null, 
        error: null,
        // Use computed title so it matches what the PDF shows
        customTitle: computedTitle,
        customSubtitle: subtitle || '',
        titleModified: false,
        selectedColumns: getDefaultColumns(reportType, claimsType),
      });
    }
    
    // Update maxRows when totalCount or chartData changes
    if (totalCount !== prevProps.totalCount ||
        (chartData && chartData.length) !== (prevProps.chartData && prevProps.chartData.length)) {
      const cap = this.getValidMaxRows(totalCount, chartData);
      const { defaultMaxRows } = this.props;
      const newMaxRows = (defaultMaxRows && defaultMaxRows > 0)
        ? Math.min(defaultMaxRows, cap)
        : cap;
      if (newMaxRows !== options.maxRows) {
        this.setState(prev => ({
          options: {
            ...prev.options,
            maxRows: newMaxRows,
            ...defaultOptions,
          },
        }));
      }
    }
    
    // Generate preview when dialog opens or any relevant option changes
    if (open && chartData && chartData.length > 0) {
      const optionsChanged = 
        pdfType !== prevState.pdfType ||
        options.maxRows !== prevState.options.maxRows;
      
      const dataChanged = 
        reportType !== prevProps.reportType ||
        chartData !== prevProps.chartData;
      
      // Also regenerate when title or subtitle changes (with debounce)
      const titleChanged = 
        customTitle !== prevState.customTitle ||
        customSubtitle !== prevState.customSubtitle;
      
      // Regenerate when column selection changes (use deep comparison)
      const columnsChanged = JSON.stringify(selectedColumns) !== JSON.stringify(prevState.selectedColumns);
      
      if ((open && !prevProps.open) || optionsChanged || dataChanged || columnsChanged) {
        clearTimeout(this.generatePreviewTimer);
        this.generatePreviewTimer = setTimeout(() => this.generatePreview(), 300);
      } else if (titleChanged && titleModified) {
        // Debounce title/subtitle changes with a longer delay
        clearTimeout(this.titleChangeTimer);
        this.titleChangeTimer = setTimeout(() => this.generatePreview(), 800);
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.generatePreviewTimer);
    clearTimeout(this.titleChangeTimer);
    if (this.state.previewUrl) {
      URL.revokeObjectURL(this.state.previewUrl);
    }
  }

  generatePreview = async () => {
    const { open, reportType, chartData, providerCounts, tableData, columns, filters, payors, defaultOptions } = this.props;
    const { options, pdfType, previewUrl, customTitle, customSubtitle, selectedColumns, customPages } = this.state;
    
    if (!open) return;
    
    this.setState({ isGenerating: true, error: null });
    
    try {
      // Clean up previous preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      const pdfConfig = PDF_TYPES[pdfType];
      const pdfOptions = {
        ...options,
        ...defaultOptions,
        orientation: pdfConfig.orientation,
        pageSize: pdfConfig.pageSize,
        pdfType,  // Pass pdfType for style selection
        selectedColumns,  // Pass column selection (must be after defaultOptions spread)
      };

      const url = await previewMarketDataPDF({
        title: customTitle,  // Use the editable title
        subtitle: customSubtitle,  // Use the editable subtitle
        reportType,
        chartData,
        providerCounts,
        tableData,
        columns,
        filters,
        payors,
        options: pdfOptions,
        customPages,  // Pass custom pages
      });

      this.setState({ previewUrl: url, isGenerating: false });
    } catch (err) {
      console.error('Failed to generate PDF preview:', err);
      this.setState({ 
        error: `Failed to generate PDF preview: ${err.message}`, 
        isGenerating: false,
        previewUrl: null,
      });
    }
  };

  handleDownload = async () => {
    const { reportType, chartData, providerCounts, tableData, columns, filters, payors, defaultOptions } = this.props;
    const { options, pdfType, customTitle, customSubtitle, selectedColumns, customPages } = this.state;

    this.setState({ isGenerating: true, error: null });

    try {
      const pdfConfig = PDF_TYPES[pdfType];
      const pdfOptions = {
        ...options,
        ...defaultOptions,
        orientation: pdfConfig.orientation,
        pageSize: pdfConfig.pageSize,
        pdfType,  // Pass pdfType for style selection
        selectedColumns,  // Pass column selection
      };

      // Build filename: Doctivity-[Tab]-[Market]-[Date w/ Time].pdf
      const tabNames = {
        'projected_patients': 'Projected-Patients',
        'captured_claims': 'Captured-Claims',
        'shared_affiliation': 'Shared-Affiliation',
        'shared_patients': 'Shared-Patients',
        'payors': 'Payors',
        'payor_mix': 'Payors',
      };
      const tabName = tabNames[reportType] || 'Market-Data';
      const now = new Date();
      const dateTime = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      const sanitize = (s) => String(s || '').replace(/[^a-zA-Z0-9]/g, '-');

      let filename;
      if (reportType === 'shared_patients') {
        // Per-provider report — name by direction + provider instead of market.
        const direction = filters?.before ? 'Before' : 'After';
        const providerName = sanitize(
          [filters?.provider?.last_name, filters?.provider?.first_name]
            .filter(Boolean)
            .join('-'),
        ) || 'Provider';
        if (filters?.provider2) {
          // Drill-down (service lines for a provider pair)
          const provider2Name = sanitize(
            [filters.provider2.last_name, filters.provider2.first_name]
              .filter(Boolean)
              .join('-'),
          ) || 'Provider';
          filename = `Doctivity-Shared-Service-Lines-${direction}-${providerName}-and-${provider2Name}-${dateTime}.pdf`;
        } else {
          filename = `Doctivity-${tabName}-${direction}-${providerName}-${dateTime}.pdf`;
        }
      } else if (reportType === 'shared_affiliation' && filters?.provider) {
        // Per-provider affiliation report (Provider > Shared Patients page) —
        // name by direction + provider instead of market.
        const direction = filters?.before ? 'Before' : 'After';
        const providerName = sanitize(
          [filters?.provider?.last_name, filters?.provider?.first_name]
            .filter(Boolean)
            .join('-'),
        ) || 'Provider';
        filename = `Doctivity-${tabName}-${direction}-${providerName}-${dateTime}.pdf`;
      } else {
        const marketName = sanitize(
          filters?.market?.name || filters?.market?.label || filters?.market || 'All',
        );
        filename = `Doctivity-${tabName}-${marketName}-${dateTime}.pdf`;
      }

      await downloadMarketDataPDF({
        title: customTitle,  // Use the editable title
        subtitle: customSubtitle,  // Use the editable subtitle
        reportType,
        chartData,
        providerCounts,
        tableData,
        columns,
        filters,
        payors,
        options: pdfOptions,
        customPages,  // Pass custom pages
        filename,
      });
      
      this.setState({ isGenerating: false });
    } catch (err) {
      console.error('Failed to download PDF:', err);
      this.setState({ 
        error: `Failed to download PDF: ${err.message}`, 
        isGenerating: false,
      });
    }
  };

  handlePdfTypeChange = (newType) => {
    const pdfConfig = PDF_TYPES[newType];
    this.setState(prev => ({
      pdfType: newType,
      options: {
        ...prev.options,
        orientation: pdfConfig.orientation,
        pageSize: pdfConfig.pageSize,
      },
    }));
  };

  handleMaxRowsChange = (value) => {
    const cap = this.getTotalCount() || 200;
    const maxRows = Math.min(cap, Math.max(1, parseInt(value, 10) || 1));
    this.setState(prev => ({
      options: {
        ...prev.options,
        maxRows,
      },
    }));
  };

  handleTitleChange = (event) => {
    this.setState({
      customTitle: event.target.value,
      titleModified: true,
    });
  };

  handleSubtitleChange = (event) => {
    this.setState({
      customSubtitle: event.target.value,
      titleModified: true,
    });
  };

  handleColumnToggle = (columnId) => {
    this.setState(prev => ({
      selectedColumns: {
        ...prev.selectedColumns,
        [columnId]: !prev.selectedColumns[columnId],
      },
    }));
  };

  getAvailableColumns = () => {
    const { reportType, defaultOptions } = this.props;
    const claimsType = defaultOptions?.claimsType || 'providers';
    const config = COLUMN_CONFIGS[reportType] || COLUMN_CONFIGS.projected_patients;
    
    if (claimsType === 'servicelines' && config.servicelines) {
      return config.servicelines;
    } else if (claimsType === 'organizations') {
      return config.organizations;
    } else {
      return config.providers;
    }
  };

  handleRefresh = () => {
    // Regenerate preview
    this.generatePreview();
  };

  handleClose = () => {
    const { onClose, defaultOptions, reportType, totalCount, chartData, defaultMaxRows } = this.props;

    // Determine claimsType for column config selection
    const claimsType = defaultOptions?.claimsType || 'providers';
    const rptType = reportType || 'projected_patients';

    // Calculate max rows (same logic as constructor) — no 200 ceiling
    let count = totalCount;
    if (!count || count < 1) {
      count = (chartData && chartData.length) || 200;
    }
    const maxRows = (defaultMaxRows && defaultMaxRows > 0)
      ? Math.min(defaultMaxRows, count)
      : count;
    
    // Reset state to initial values
    this.setState({
      pdfType: 'printable',
      customTitle: '',
      customSubtitle: '',
      titleModified: false,
      selectedColumns: getDefaultColumns(rptType, claimsType),
      options: {
        orientation: 'landscape',
        pageSize: 'letter',
        includeHeader: true,
        includeFooter: true,
        includeChart: true,
        includeTable: false,
        maxRows: maxRows,
        ...defaultOptions,
      },
      customPages: [],
      editingPage: null,
      isGeneratingAI: false,
      columnsDialogOpen: false,
      pagesDialogOpen: false,
      previewUrl: null,
      isGenerating: false,
      error: null,
    });
    
    if (onClose) {
      onClose();
    }
  };

  // === Custom Pages Handlers ===
  handleAddPage = (position) => {
    const { customTitle, customSubtitle } = this.state;
    
    const newPage = {
      id: `page-${Date.now()}`,
      position,
      title: customTitle || '',  // Default to report title
      subtitle: customSubtitle || '',  // Default to report subtitle
      components: [],  // Array of { id, type, content, settings }
    };

    this.setState(prev => ({
      customPages: [...prev.customPages, newPage],
      editingPage: newPage.id,
    }));
  };

  handleAddComponent = (pageId, componentType) => {
    const newComponent = {
      id: `comp-${Date.now()}`,
      type: componentType,
      content: '',
      settings: {},
    };

    // Set defaults based on type
    switch (componentType) {
      case 'text':
        newComponent.content = '';
        newComponent.settings = { 
          fontSize: 12, 
          bold: false, 
          align: 'left', 
        };
        break;
      case 'image':
        newComponent.content = null; // imageData
        newComponent.settings = {}; // No settings - always centered, full width
        break;
      case 'metrics':
        newComponent.content = [
          { label: 'Total Providers', value: '—' },
          { label: 'Affiliated', value: '—' },
          { label: 'Market Share', value: '—' },
        ];
        newComponent.settings = { columns: 3 };
        break;
      case 'spacer':
        newComponent.settings = { height: 20 };
        break;
      default:
        break;
    }

    this.setState(prev => ({
      customPages: prev.customPages.map(p => {
        if (p.id !== pageId) return p;
        return { ...p, components: [...p.components, newComponent] };
      }),
    }));
  };

  handleUpdateComponent = (pageId, componentId, updates) => {
    this.setState(prev => ({
      customPages: prev.customPages.map(p => {
        if (p.id !== pageId) return p;
        return {
          ...p,
          components: p.components.map(c => {
            if (c.id !== componentId) return c;
            return { ...c, ...updates };
          }),
        };
      }),
    }));
  };

  handleDeleteComponent = (pageId, componentId) => {
    this.setState(prev => ({
      customPages: prev.customPages.map(p => {
        if (p.id !== pageId) return p;
        return {
          ...p,
          components: p.components.filter(c => c.id !== componentId),
        };
      }),
    }));
  };

  handleMoveComponent = (pageId, componentId, direction) => {
    this.setState(prev => ({
      customPages: prev.customPages.map(p => {
        if (p.id !== pageId) return p;
        const components = [...p.components];
        const idx = components.findIndex(c => c.id === componentId);
        if (idx < 0) return p;
        
        const newIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= components.length) return p;
        
        [components[idx], components[newIdx]] = [components[newIdx], components[idx]];
        return { ...p, components };
      }),
    }));
  };

  handleComponentImageUpload = (pageId, componentId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      
      if (isSvg) {
        // Convert SVG to PNG using canvas
        const img = new Image();
        img.onload = () => {
          // Create canvas with good resolution
          const scale = 2; // 2x for better quality
          const canvas = document.createElement('canvas');
          canvas.width = img.width * scale || 800;
          canvas.height = img.height * scale || 600;
          
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = 'white'; // White background for transparency
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0);
          
          const pngDataUrl = canvas.toDataURL('image/png');
          this.handleUpdateComponent(pageId, componentId, { content: pngDataUrl });
        };
        img.onerror = () => {
          console.error('Failed to load SVG');
          // Try using it directly as fallback
          this.handleUpdateComponent(pageId, componentId, { content: dataUrl });
        };
        img.src = dataUrl;
      } else {
        // Regular image, use directly
        this.handleUpdateComponent(pageId, componentId, { content: dataUrl });
      }
    };
    reader.readAsDataURL(file);
  };

  handleSuggestComponentText = async (pageId, componentId) => {
    const { chartData, reportType, filters, providerCounts, payors } = this.props;
    const { customTitle } = this.state;

    this.setState({ isGeneratingAI: true });

    try {
      const dataRows = Array.isArray(chartData) ? chartData : (chartData?.rows || []);
      const totalProviders = providerCounts?.total || dataRows.length;
      const affiliatedProviders = providerCounts?.total_affiliated || 
        dataRows.filter(r => r.is_affiliated || r.provider?.is_affiliated).length;
      const affiliationPct = totalProviders > 0 
        ? Math.round((affiliatedProviders / totalProviders) * 100) 
        : 0;

      const specialtyCounts = {};
      dataRows.slice(0, 50).forEach(row => {
        const spec = row.provider?.specialty || row.specialty || 'Unknown';
        specialtyCounts[spec] = (specialtyCounts[spec] || 0) + 1;
      });
      const topSpecialties = Object.entries(specialtyCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name]) => name);

      const marketName = filters?.market?.name || filters?.market?.label || 
        (typeof filters?.market === 'string' ? filters.market : 'the selected market');

      const reportTypeLabels = {
        projected_patients: 'Projected Patients',
        captured_claims: 'Captured Claims',
        shared_affiliation: 'Shared Affiliation',
        shared_patients: 'Shared Patients',
        payors: 'Payor Mix',
        payor_mix: 'Payor Mix',
      };
      const reportLabel = reportTypeLabels[reportType] || 'Market Data';

      const prompt = `Generate content for a healthcare market analysis slide.

Report: ${customTitle || reportLabel}
Market: ${marketName}
Total Providers: ${totalProviders.toLocaleString()}
Affiliated: ${affiliatedProviders.toLocaleString()} (${affiliationPct}%)
Top Specialties: ${topSpecialties.join(', ') || 'Various'}
${payors?.length > 0 ? `Payors: ${payors.map(p => p.name).join(', ')}` : ''}

Generate 3-4 bullet points with key insights. Start each with •. Be specific with numbers.`;

      const response = await axiosInstance.post('/AI', {
        type: 'pdf-suggest',
        prompt,
        contentType: 'paragraph',
      });

      const result = response.data;
      const suggestedContent = result.summary || result.text || result.content || '';

      this.handleUpdateComponent(pageId, componentId, { content: suggestedContent });
      this.setState({ isGeneratingAI: false });
    } catch (err) {
      console.error('AI suggestion failed:', err);
      // Fallback content
      const dataRows = Array.isArray(chartData) ? chartData : (chartData?.rows || []);
      const totalProviders = providerCounts?.total || dataRows.length;
      const affiliatedProviders = providerCounts?.total_affiliated || 
        dataRows.filter(r => r.is_affiliated || r.provider?.is_affiliated).length;
      const affiliationPct = totalProviders > 0 
        ? Math.round((affiliatedProviders / totalProviders) * 100) 
        : 0;

      const fallbackContent = `• Analysis covers ${totalProviders.toLocaleString()} providers in the market

• ${affiliatedProviders.toLocaleString()} providers (${affiliationPct}%) are currently affiliated

• Opportunity exists among ${(totalProviders - affiliatedProviders).toLocaleString()} non-affiliated providers`;

      this.handleUpdateComponent(pageId, componentId, { content: fallbackContent });
      this.setState({ isGeneratingAI: false });
    }
  };

  handlePopulateMetrics = (pageId, componentId) => {
    const { chartData, providerCounts } = this.props;
    
    const dataRows = Array.isArray(chartData) ? chartData : (chartData?.rows || []);
    const totalProviders = providerCounts?.total || dataRows.length;
    const affiliatedProviders = providerCounts?.total_affiliated || 
      dataRows.filter(r => r.is_affiliated || r.provider?.is_affiliated).length;
    const affiliationPct = totalProviders > 0 
      ? Math.round((affiliatedProviders / totalProviders) * 100) 
      : 0;

    const metrics = [
      { label: 'Total Providers', value: totalProviders.toLocaleString() },
      { label: 'Affiliated', value: affiliatedProviders.toLocaleString() },
      { label: 'Affiliation Rate', value: `${affiliationPct}%` },
    ];

    this.handleUpdateComponent(pageId, componentId, { content: metrics });
  };

  handleEditPage = (pageId) => {
    this.setState({ editingPage: pageId });
  };

  handleDeletePage = (pageId) => {
    this.setState(prev => ({
      customPages: prev.customPages.filter(p => p.id !== pageId),
    }), () => {
      this.generatePreview();
    });
  };

  handleMovePage = (pageId, direction) => {
    this.setState(prev => {
      const pages = [...prev.customPages];
      const idx = pages.findIndex(p => p.id === pageId);
      if (idx < 0) return prev;
      
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= pages.length) return prev;
      
      [pages[idx], pages[newIdx]] = [pages[newIdx], pages[idx]];
      return { customPages: pages };
    }, () => {
      this.generatePreview();
    });
  };

  handlePageFieldChange = (pageId, field, value) => {
    this.setState(prev => ({
      customPages: prev.customPages.map(p => 
        p.id === pageId ? { ...p, [field]: value } : p
      ),
    }));
  };

  handleSavePageEdit = () => {
    this.setState({ editingPage: null }, () => {
      this.generatePreview();
    });
  };

  handleCancelPageEdit = () => {
    const { editingPage, customPages } = this.state;
    // If it's a new page that hasn't been saved, remove it
    const page = customPages.find(p => p.id === editingPage);
    if (page && !page.title && !page.content) {
      this.setState(prev => ({
        customPages: prev.customPages.filter(p => p.id !== editingPage),
        editingPage: null,
      }));
    } else {
      this.setState({ editingPage: null });
    }
  };

  handleImageUpload = (pageId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.handlePageFieldChange(pageId, 'imageData', e.target.result);
    };
    reader.readAsDataURL(file);
  };

  handleSuggestSummary = async (pageId) => {
    const { chartData, reportType, filters, providerCounts, payors } = this.props;
    const { customTitle } = this.state;

    this.setState({ isGeneratingAI: true });

    try {
      // Build context about the report data
      const dataRows = Array.isArray(chartData) ? chartData : (chartData?.rows || []);
      const totalProviders = providerCounts?.total || dataRows.length;
      const affiliatedProviders = providerCounts?.total_affiliated || 
        dataRows.filter(r => r.is_affiliated || r.provider?.is_affiliated).length;
      const affiliationPct = totalProviders > 0 
        ? Math.round((affiliatedProviders / totalProviders) * 100) 
        : 0;

      // Get top specialties
      const specialtyCounts = {};
      dataRows.slice(0, 50).forEach(row => {
        const spec = row.provider?.specialty || row.specialty || 'Unknown';
        specialtyCounts[spec] = (specialtyCounts[spec] || 0) + 1;
      });
      const topSpecialties = Object.entries(specialtyCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name]) => name);

      // Get market name
      const marketName = filters?.market?.name || filters?.market?.label || 
        (typeof filters?.market === 'string' ? filters.market : 'the selected market');

      // Build the prompt
      const reportTypeLabels = {
        projected_patients: 'Projected Patients',
        captured_claims: 'Captured Claims',
        shared_affiliation: 'Shared Affiliation',
        shared_patients: 'Shared Patients',
        payors: 'Payor Mix',
        payor_mix: 'Payor Mix',
      };
      const reportLabel = reportTypeLabels[reportType] || 'Market Data';

      const prompt = `Generate a brief executive summary (2-3 sentences) for a healthcare market analysis report.

Report: ${customTitle || reportLabel}
Market: ${marketName}
Total Providers: ${totalProviders.toLocaleString()}
Affiliated Providers: ${affiliatedProviders.toLocaleString()} (${affiliationPct}%)
Top Specialties: ${topSpecialties.join(', ') || 'Various'}
${payors?.length > 0 ? `Payors Analyzed: ${payors.map(p => p.name).join(', ')}` : ''}

Write a professional summary highlighting key insights. Be specific with numbers. Format as a brief paragraph.`;

      // Call the AI API (using the app's axiosInstance for auth)
      const response = await axiosInstance.post('/AI', {
        type: 'pdf-suggest',
        prompt,
        contentType: 'exec_summary',
      });

      const result = response.data;
      const suggestedSummary = result.summary || result.text || result.content || '';

      // Update the page with suggested content
      this.setState(prev => ({
        customPages: prev.customPages.map(p => {
          if (p.id !== pageId) return p;
          return {
            ...p,
            title: p.title || 'Executive Summary',
            content: suggestedSummary,
            metrics: [
              { label: 'Total Providers', value: totalProviders.toLocaleString() },
              { label: 'Affiliated', value: `${affiliatedProviders.toLocaleString()} (${affiliationPct}%)` },
              { label: 'Top Specialty', value: topSpecialties[0] || '—' },
            ],
          };
        }),
        isGeneratingAI: false,
      }));
    } catch (err) {
      console.error('AI suggestion failed:', err);
      
      // Fallback: generate a basic summary without AI
      const dataRows = Array.isArray(chartData) ? chartData : (chartData?.rows || []);
      const totalProviders = providerCounts?.total || dataRows.length;
      const affiliatedProviders = providerCounts?.total_affiliated || 
        dataRows.filter(r => r.is_affiliated || r.provider?.is_affiliated).length;
      const affiliationPct = totalProviders > 0 
        ? Math.round((affiliatedProviders / totalProviders) * 100) 
        : 0;
      const marketName = filters?.market?.name || filters?.market?.label || 'the selected market';

      const fallbackSummary = `This report analyzes ${totalProviders.toLocaleString()} providers in ${marketName}. Of these, ${affiliatedProviders.toLocaleString()} (${affiliationPct}%) are affiliated with your organization.`;

      this.setState(prev => ({
        customPages: prev.customPages.map(p => {
          if (p.id !== pageId) return p;
          return {
            ...p,
            title: p.title || 'Executive Summary',
            content: fallbackSummary,
            metrics: [
              { label: 'Total Providers', value: totalProviders.toLocaleString() },
              { label: 'Affiliated', value: `${affiliatedProviders.toLocaleString()} (${affiliationPct}%)` },
              { label: 'Affiliation Rate', value: `${affiliationPct}%` },
            ],
          };
        }),
        isGeneratingAI: false,
      }));
    }
  };

  handleSuggestParagraph = async (pageId) => {
    const { chartData, reportType, filters, providerCounts, payors } = this.props;
    const { customTitle, customPages } = this.state;

    const page = customPages.find(p => p.id === pageId);
    if (!page) return;

    this.setState({ isGeneratingAI: true });

    try {
      // Build context about the report data
      const dataRows = Array.isArray(chartData) ? chartData : (chartData?.rows || []);
      const totalProviders = providerCounts?.total || dataRows.length;
      const affiliatedProviders = providerCounts?.total_affiliated || 
        dataRows.filter(r => r.is_affiliated || r.provider?.is_affiliated).length;
      const affiliationPct = totalProviders > 0 
        ? Math.round((affiliatedProviders / totalProviders) * 100) 
        : 0;

      // Get top specialties
      const specialtyCounts = {};
      dataRows.slice(0, 50).forEach(row => {
        const spec = row.provider?.specialty || row.specialty || 'Unknown';
        specialtyCounts[spec] = (specialtyCounts[spec] || 0) + 1;
      });
      const topSpecialties = Object.entries(specialtyCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name]) => name);

      const marketName = filters?.market?.name || filters?.market?.label || 
        (typeof filters?.market === 'string' ? filters.market : 'the selected market');

      const reportTypeLabels = {
        projected_patients: 'Projected Patients',
        captured_claims: 'Captured Claims',
        shared_affiliation: 'Shared Affiliation',
        shared_patients: 'Shared Patients',
        payors: 'Payor Mix',
        payor_mix: 'Payor Mix',
      };
      const reportLabel = reportTypeLabels[reportType] || 'Market Data';

      const prompt = `Generate bullet points for a healthcare market analysis slide.

Report: ${customTitle || reportLabel}
Page Title: ${page.title || 'Key Findings'}
Market: ${marketName}
Total Providers: ${totalProviders.toLocaleString()}
Affiliated: ${affiliatedProviders.toLocaleString()} (${affiliationPct}%)
Top Specialties: ${topSpecialties.join(', ') || 'Various'}
${payors?.length > 0 ? `Payors: ${payors.map(p => p.name).join(', ')}` : ''}

Generate 4-5 bullet points with key insights. Start each with •. Be specific with numbers. Keep each bullet to 1-2 sentences.`;

      // Call the AI API (using the app's axiosInstance for auth)
      const response = await axiosInstance.post('/AI', {
        type: 'pdf-suggest',
        prompt,
        contentType: 'paragraph',
      });

      const result = response.data;
      const suggestedContent = result.summary || result.text || result.content || '';

      this.setState(prev => ({
        customPages: prev.customPages.map(p => 
          p.id === pageId ? { ...p, content: suggestedContent } : p
        ),
        isGeneratingAI: false,
      }));
    } catch (err) {
      console.error('AI suggestion failed:', err);
      
      // Fallback: generate basic bullets without AI
      const dataRows = Array.isArray(chartData) ? chartData : (chartData?.rows || []);
      const totalProviders = providerCounts?.total || dataRows.length;
      const affiliatedProviders = providerCounts?.total_affiliated || 
        dataRows.filter(r => r.is_affiliated || r.provider?.is_affiliated).length;
      const affiliationPct = totalProviders > 0 
        ? Math.round((affiliatedProviders / totalProviders) * 100) 
        : 0;
      const marketName = filters?.market?.name || filters?.market?.label || 'the selected market';

      const fallbackContent = `• Analysis covers ${totalProviders.toLocaleString()} providers in ${marketName}

• ${affiliatedProviders.toLocaleString()} providers (${affiliationPct}%) are currently affiliated with your organization

• Opportunity exists to grow affiliation among the remaining ${(totalProviders - affiliatedProviders).toLocaleString()} non-affiliated providers

• Focus outreach efforts on high-volume specialties to maximize patient capture potential`;

      this.setState(prev => ({
        customPages: prev.customPages.map(p => 
          p.id === pageId ? { ...p, content: fallbackContent } : p
        ),
        isGeneratingAI: false,
      }));
    }
  };

  renderColumnsDialog = () => {
    const { columnsDialogOpen, selectedColumns } = this.state;
    const availableColumns = this.getAvailableColumns();

    return (
      <Dialog 
        open={columnsDialogOpen} 
        onClose={() => this.setState({ columnsDialogOpen: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Select Columns</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {availableColumns.map(col => (
              <FormControlLabel
                key={col.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedColumns[col.id] || false}
                    onChange={() => this.handleColumnToggle(col.id)}
                  />
                }
                label={col.label}
                sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.setState({ columnsDialogOpen: false })} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderPagesDialog = () => {
    const { pagesDialogOpen, customPages } = this.state;

    return (
      <Dialog 
        open={pagesDialogOpen} 
        onClose={() => this.setState({ pagesDialogOpen: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Custom Pages</span>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              this.handleAddPage('before');
              // Keep pages dialog open, page editor will open on top
            }}
            sx={{ textTransform: 'none' }}
          >
            Add Page
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {customPages.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No custom pages yet. Add pages to insert before or after your data.
              </Typography>
            </Box>
          ) : (
            <List dense>
              {customPages.map((page, idx) => {
                const componentCount = (page.components || []).length;
                return (
                  <ListItem 
                    key={page.id}
                    sx={{ 
                      border: '1px solid', 
                      borderColor: 'grey.200', 
                      borderRadius: 1, 
                      mb: 1,
                      bgcolor: 'grey.50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      pr: 1,
                    }}
                  >
                    <Box 
                      sx={{ 
                        px: 1, 
                        py: 0.25, 
                        bgcolor: page.position === 'before' ? 'info.100' : 'success.100',
                        color: page.position === 'before' ? 'info.800' : 'success.800',
                        borderRadius: 0.5,
                        fontSize: 11,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {page.position === 'before' ? '↑ Before' : '↓ After'}
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {page.title || '(Untitled)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 1 }}>
                      {componentCount} {componentCount === 1 ? 'item' : 'items'}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => this.handleMovePage(page.id, 'up')}
                      disabled={idx === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => this.handleMovePage(page.id, 'down')}
                      disabled={idx === customPages.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => this.handleEditPage(page.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => this.handleDeletePage(page.id)} sx={{ color: 'error.main' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.setState({ pagesDialogOpen: false })} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderPageEditor = () => {
    const { classes } = this.props;
    const { editingPage, customPages, isGeneratingAI } = this.state;
    
    if (!editingPage) return null;
    
    const page = customPages.find(p => p.id === editingPage);
    if (!page) return null;

    // Ensure components array exists (for backward compatibility)
    const components = page.components || [];

    return (
      <Dialog open={true} onClose={this.handleCancelPageEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Edit Custom Page</span>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Select
              size="small"
              value={page.position}
              onChange={(e) => this.handlePageFieldChange(page.id, 'position', e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="before">Insert Before Data</MenuItem>
              <MenuItem value="after">Insert After Data</MenuItem>
            </Select>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Page Title & Subtitle */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              label="Page Title"
              value={page.title || ''}
              onChange={(e) => this.handlePageFieldChange(page.id, 'title', e.target.value)}
              size="small"
            />
            <TextField
              fullWidth
              label="Subtitle (optional)"
              value={page.subtitle || ''}
              onChange={(e) => this.handlePageFieldChange(page.id, 'subtitle', e.target.value)}
              size="small"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Add Component Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ mr: 1, alignSelf: 'center', color: 'text.secondary' }}>
              Add:
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => this.handleAddComponent(page.id, 'text')}
              sx={{ textTransform: 'none' }}
            >
              + Text Block
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => this.handleAddComponent(page.id, 'image')}
              sx={{ textTransform: 'none' }}
            >
              + Image
            </Button>
            {/* Metric Cards button hidden for now
            <Button
              size="small"
              variant="outlined"
              onClick={() => this.handleAddComponent(page.id, 'metrics')}
              sx={{ textTransform: 'none' }}
            >
              + Metric Cards
            </Button>
            */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => this.handleAddComponent(page.id, 'spacer')}
              sx={{ textTransform: 'none' }}
            >
              + Spacer
            </Button>
          </Box>

          {/* Components List */}
          {components.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1, border: '2px dashed', borderColor: 'grey.300' }}>
              <Typography color="text.secondary">
                No components yet. Add text or images above.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {components.map((comp, idx) => (
                <Box 
                  key={comp.id} 
                  sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: 'grey.300', 
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}
                >
                  {/* Component Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                      {comp.type === 'text' && 'Text Block'}
                      {comp.type === 'image' && 'Image'}
                      {comp.type === 'metrics' && 'Metric Cards'}
                      {comp.type === 'spacer' && 'Spacer'}
                    </Typography>
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => this.handleMoveComponent(page.id, comp.id, 'up')}
                        disabled={idx === 0}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => this.handleMoveComponent(page.id, comp.id, 'down')}
                        disabled={idx === components.length - 1}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => this.handleDeleteComponent(page.id, comp.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Text Component */}
                  {comp.type === 'text' && (
                    <Box>
                      {/* Controls: Font size, Bold, Alignment, AI */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Select
                          size="small"
                          value={comp.settings?.fontSize || 12}
                          onChange={(e) => this.handleUpdateComponent(page.id, comp.id, { 
                            settings: { ...comp.settings, fontSize: e.target.value } 
                          })}
                          sx={{ minWidth: 75 }}
                        >
                          <MenuItem value={10}>10pt</MenuItem>
                          <MenuItem value={11}>11pt</MenuItem>
                          <MenuItem value={12}>12pt</MenuItem>
                          <MenuItem value={14}>14pt</MenuItem>
                          <MenuItem value={16}>16pt</MenuItem>
                          <MenuItem value={18}>18pt</MenuItem>
                          <MenuItem value={24}>24pt</MenuItem>
                        </Select>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={comp.settings?.bold || false}
                              onChange={(e) => this.handleUpdateComponent(page.id, comp.id, { 
                                settings: { ...comp.settings, bold: e.target.checked } 
                              })}
                            />
                          }
                          label="Bold"
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 600 } }}
                        />
                        <Select
                          size="small"
                          value={comp.settings?.align || 'left'}
                          onChange={(e) => this.handleUpdateComponent(page.id, comp.id, { 
                            settings: { ...comp.settings, align: e.target.value } 
                          })}
                          sx={{ minWidth: 90 }}
                        >
                          <MenuItem value="left">Left</MenuItem>
                          <MenuItem value="center">Center</MenuItem>
                          <MenuItem value="right">Right</MenuItem>
                        </Select>
                        <Box sx={{ flex: 1 }} />
                        {/* AI Suggest button hidden for now - function still available
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={isGeneratingAI ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                          onClick={() => this.handleSuggestComponentText(page.id, comp.id)}
                          disabled={isGeneratingAI}
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          {isGeneratingAI ? 'Generating...' : 'AI Suggest'}
                        </Button>
                        */}
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={comp.content || ''}
                        onChange={(e) => this.handleUpdateComponent(page.id, comp.id, { content: e.target.value })}
                        placeholder="Enter text content. Lines starting with • - * will be formatted as bullets."
                        size="small"
                        rows={8}
                      />
                    </Box>
                  )}

                  {/* Image Component */}
                  {comp.type === 'image' && (
                    <Box>
                      {!comp.content ? (
                        <Box>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{ textTransform: 'none' }}
                          >
                            Choose Image
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => this.handleComponentImageUpload(page.id, comp.id, e)}
                            />
                          </Button>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Image will be centered at full width (but never larger than original size)
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Box sx={{ textAlign: 'center', mb: 1 }}>
                            <img 
                              src={comp.content} 
                              alt="Preview" 
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: 150,
                                border: '1px solid #ddd',
                                borderRadius: 4,
                              }} 
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              sx={{ textTransform: 'none' }}
                            >
                              Change Image
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => this.handleComponentImageUpload(page.id, comp.id, e)}
                              />
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => this.handleUpdateComponent(page.id, comp.id, { content: null })}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Metrics Component */}
                  {comp.type === 'metrics' && (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => this.handlePopulateMetrics(page.id, comp.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          Populate from Data
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          or edit manually below
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {(Array.isArray(comp.content) ? comp.content : []).map((metric, mIdx) => (
                          <Box 
                            key={mIdx} 
                            sx={{ 
                              p: 1.5, 
                              bgcolor: 'white', 
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              minWidth: 120,
                            }}
                          >
                            <TextField
                              size="small"
                              value={metric.value || ''}
                              onChange={(e) => {
                                const newMetrics = [...comp.content];
                                newMetrics[mIdx] = { ...metric, value: e.target.value };
                                this.handleUpdateComponent(page.id, comp.id, { content: newMetrics });
                              }}
                              placeholder="Value"
                              sx={{ mb: 0.5, '& input': { textAlign: 'center', fontWeight: 600 } }}
                              fullWidth
                            />
                            <TextField
                              size="small"
                              value={metric.label || ''}
                              onChange={(e) => {
                                const newMetrics = [...comp.content];
                                newMetrics[mIdx] = { ...metric, label: e.target.value };
                                this.handleUpdateComponent(page.id, comp.id, { content: newMetrics });
                              }}
                              placeholder="Label"
                              sx={{ '& input': { textAlign: 'center', fontSize: 12 } }}
                              fullWidth
                            />
                          </Box>
                        ))}
                        <Button
                          size="small"
                          onClick={() => {
                            const newMetrics = [...(comp.content || []), { label: 'New Metric', value: '—' }];
                            this.handleUpdateComponent(page.id, comp.id, { content: newMetrics });
                          }}
                          sx={{ alignSelf: 'center' }}
                        >
                          + Add Card
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Spacer Component */}
                  {comp.type === 'spacer' && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="caption">Height:</Typography>
                      <Select
                        size="small"
                        value={comp.settings?.heightType === 'percent' ? 'percent' : (comp.settings?.height || 20)}
                        onChange={(e) => {
                          if (e.target.value === 'percent') {
                            this.handleUpdateComponent(page.id, comp.id, { 
                              settings: { ...comp.settings, heightType: 'percent', heightPercent: comp.settings?.heightPercent || 10 } 
                            });
                          } else {
                            this.handleUpdateComponent(page.id, comp.id, { 
                              settings: { ...comp.settings, heightType: 'fixed', height: e.target.value } 
                            });
                          }
                        }}
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value={10}>Small (10pt)</MenuItem>
                        <MenuItem value={20}>Medium (20pt)</MenuItem>
                        <MenuItem value={40}>Large (40pt)</MenuItem>
                        <MenuItem value={60}>Extra Large (60pt)</MenuItem>
                        <MenuItem value="percent">% of page</MenuItem>
                      </Select>
                      {comp.settings?.heightType === 'percent' && (
                        <TextField
                          size="small"
                          type="number"
                          value={comp.settings?.heightPercent || 10}
                          onChange={(e) => this.handleUpdateComponent(page.id, comp.id, { 
                            settings: { ...comp.settings, heightPercent: Math.min(100, Math.max(1, parseInt(e.target.value) || 10)) } 
                          })}
                          inputProps={{ min: 1, max: 100, style: { width: 50 } }}
                          sx={{ width: 80 }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCancelPageEdit}>Cancel</Button>
          <Button onClick={this.handleSavePageEdit} variant="contained" color="primary">
            Save & Preview
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const { classes, open, chartData, reportType, defaultOptions } = this.props;
    const { pdfType, options, previewUrl, isGenerating, error, customTitle, customSubtitle, selectedColumns, customPages, columnsDialogOpen, pagesDialogOpen } = this.state;
    const displayTotalCount = this.getTotalCount();
    const availableColumns = this.getAvailableColumns();

    return (
      <Dialog
        open={open}
        onClose={this.handleClose}
        className={classes.dialog}
        maxWidth={false}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography className={classes.titleText}>
              Export to PDF
            </Typography>
            <IconButton 
              onClick={this.handleRefresh} 
              size="small"
              disabled={isGenerating}
              title="Refresh preview"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          <IconButton onClick={this.handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.content}>
          {/* Settings Bar at Top */}
          <div className={classes.settingsBar}>
            {/* Editable Title Field */}
            <Box className={classes.titleFieldContainer}>
              <Typography variant="body2" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                Title:
              </Typography>
              <TextField
                size="small"
                value={customTitle}
                onChange={this.handleTitleChange}
                className={classes.titleField}
                placeholder="Enter PDF title"
              />
            </Box>

            {/* Editable Subtitle Field */}
            <Box className={classes.titleFieldContainer}>
              <Typography variant="body2" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                Subtitle:
              </Typography>
              <TextField
                size="small"
                value={customSubtitle}
                onChange={this.handleSubtitleChange}
                className={classes.titleField}
                placeholder="Optional subtitle"
              />
            </Box>

            <Box className={classes.rowCountContainer}>
              <Typography variant="body2" style={{ fontSize: 13 }}>
                PDF Type:
              </Typography>
              <Select
                size="small"
                value={pdfType}
                onChange={(e) => this.handlePdfTypeChange(e.target.value)}
                style={{ minWidth: 160, fontSize: 13 }}
              >
                <MenuItem value="printable">Printable (Letter)</MenuItem>
                <MenuItem value="slideshow">Presentation (16:9)</MenuItem>
              </Select>
            </Box>

            <Box className={classes.rowCountContainer}>
              <Typography variant="body2" style={{ fontSize: 13 }}>
                Max rows:
              </Typography>
              <TextField
                type="number"
                size="small"
                value={options.maxRows}
                onChange={(e) => this.handleMaxRowsChange(e.target.value)}
                inputProps={{ min: 1, max: displayTotalCount || 200 }}
                className={classes.rowCountInput}
              />
              <Typography variant="caption" color="textSecondary">
                / {displayTotalCount}
              </Typography>
            </Box>

            {/* Columns and Custom Pages Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => this.setState({ columnsDialogOpen: true })}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                Columns ({Object.values(selectedColumns).filter(Boolean).length})
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  if (customPages.length === 0) {
                    // No pages - go straight to add/edit
                    this.handleAddPage('before');
                  } else {
                    // Has pages - open pages dialog
                    this.setState({ pagesDialogOpen: true });
                  }
                }}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                {customPages.length === 0 ? 'Add Page' : `Custom Pages (${customPages.length})`}
              </Button>
            </Box>
          </div>

          {/* Columns Dialog */}
          {this.renderColumnsDialog()}

          {/* Custom Pages Dialog */}
          {this.renderPagesDialog()}

          {/* Page Editor Dialog */}
          {this.renderPageEditor()}

          {/* Preview Panel */}
          <div className={classes.previewPanel}>
            <div className={classes.previewHeader}>
              <Typography className={classes.previewTitle}>
                Preview
              </Typography>
            </div>

            {isGenerating ? (
              <div className={classes.previewLoading}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="textSecondary">
                  Generating preview...
                </Typography>
              </div>
            ) : error ? (
              <div className={classes.previewEmpty}>
                <Typography color="error">{error}</Typography>
              </div>
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                className={classes.previewFrame}
                title="PDF Preview"
              />
            ) : (
              <div className={classes.previewEmpty}>
                <Typography variant="body2">
                  Click refresh to generate preview
                </Typography>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button onClick={this.handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleDownload}
            disabled={isGenerating || !chartData || chartData.length === 0}
            startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            className={classes.downloadButton}
          >
            {isGenerating ? 'Generating...' : 'Download PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

PDFExportDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  reportType: PropTypes.string,
  chartData: PropTypes.array,
  providerCounts: PropTypes.object,
  tableData: PropTypes.array,
  columns: PropTypes.array,
  filters: PropTypes.object,
  payors: PropTypes.array,
  totalCount: PropTypes.number,
  // Initial slider value (defaults to totalCount). Use when the cap should be
  // higher than the on-screen row count but the slider should start at that
  // smaller number — e.g. ShareD Patients defaults to the loaded 25 but can
  // be dialed up to the full available total.
  defaultMaxRows: PropTypes.number,
  defaultOptions: PropTypes.object,
};

PDFExportDialog.defaultProps = {
  title: 'Market Data Export',
  subtitle: '',
  reportType: 'projected_patients',
  chartData: [],
  providerCounts: null,
  tableData: [],
  columns: [],
  filters: {},
  payors: [],
  totalCount: 0,
  defaultOptions: {},
};

const StyledPDFExportDialog = withStyles(styles)(PDFExportDialog);

export { StyledPDFExportDialog as PDFExportDialog };
export default StyledPDFExportDialog;